// FeedbackList — super-admin inbox for the "Send feedback" form (ticket 123).
// GET/PATCH /api/feedback are owner-only; the host page (SuperAdminPage)
// already gates on is_super_admin. Follows AccountsOverview's Tailwind style.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Loader2, ExternalLink } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { api } from '../api';
import { fromApi } from '../kit/time';
import { TableScroller } from '@/components/ui/table-scroller';
import LongTextCell from '@/components/LongTextCell';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const STATUSES = ['new', 'seen', 'done'];

export default function FeedbackList({ limit = 10, full = false }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);

  const load = () => {
    api.get(`/feedback?limit=${limit}`)
      .then(({ data }) => setItems(data.items || []))
      .catch((e) => setError(e.response?.data?.detail || 'Could not load feedback'));
  };

  useEffect(() => { load(); }, [limit]);

  const setStatus = (id, status) => {
    const prev = items;
    setItems((cur) => cur.map((it) => (it.feedback_id === id ? { ...it, status } : it)));
    api.patch(`/feedback/${id}`, { status }).catch(() => setItems(prev));
  };

  const newCount = (items || []).filter((it) => it.status === 'new').length;
  const sentryOrg = process.env.REACT_APP_SENTRY_ORG;

  return (
    <section className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden mb-8 text-[13px]" data-testid="feedback-list">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100">
        <div className="p-2 rounded-lg" style={{ background: '#070B5F15', color: '#070B5F' }}>
          <MessageSquare size={16} />
        </div>
        <h2 className="font-serif text-lg font-bold flex items-center gap-2" style={{ color: '#070B5F' }}>
          Feedback
          {newCount > 0 && (
            <span
              className="inline-flex items-center justify-center text-[11px] font-bold rounded-full px-2 py-0.5"
              style={{ background: '#FEE2E2', color: '#991B1B' }}
              data-testid="feedback-new-count"
            >
              {newCount} new
            </span>
          )}
          {!full && (
            <Link
              to="/portal/super-admin/feedback"
              className="text-[12px] font-semibold ml-1 hover:underline"
              style={{ color: '#070B5F' }}
              data-testid="feedback-view-all"
            >
              View all &rarr;
            </Link>
          )}
        </h2>
      </div>

      {error && <div className="p-6 text-red-800" data-testid="feedback-error">{error}</div>}
      {!error && items === null && (
        <div className="p-6 text-slate-500 flex items-center gap-2" data-testid="feedback-loading">
          <Loader2 size={16} className="animate-spin" /> Loading feedback…
        </div>
      )}
      {!error && items && items.length === 0 && (
        <div className="p-6 text-slate-500 italic" data-testid="feedback-empty">No feedback yet.</div>
      )}
      {!error && items && items.length > 0 && (
        <TableScroller><table className="w-full">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest font-bold text-slate-500 border-b border-slate-100">
              <th className="text-left px-5 py-2">Created</th>
              <th className="text-left px-2 py-2">Tenant</th>
              <th className="text-left px-2 py-2">User</th>
              <th className="text-left px-2 py-2">Page</th>
              <th className="text-left px-2 py-2">Message</th>
              <th className="text-left px-2 py-2">Sentry</th>
              <th className="text-left px-5 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              return (
                <tr key={it.feedback_id} className="border-b border-slate-50 last:border-0 align-top" data-testid={`feedback-row-${it.feedback_id}`}>
                  <td className="px-5 py-2.5 text-slate-500 whitespace-nowrap">
                    {it.created_at ? formatDistanceToNow(fromApi(it.created_at), { addSuffix: true }) : '—'}
                  </td>
                  <td className="px-2 py-2.5">
                    <LongTextCell id={`feedback-tenant-${it.feedback_id}`} text={it.tenant_name || it.tenant_id} lines={2} maxWidth={200} className="text-[#070B5F]" />
                  </td>
                  <td className="px-2 py-2.5">{it.user_name || it.user_email || it.user_id}</td>
                  <td className="px-2 py-2.5 font-mono text-[12px] text-slate-500">
                    <LongTextCell id={`feedback-page-${it.feedback_id}`} text={it.page} lines={2} maxWidth={200}>
                      {it.page && it.page.startsWith('/') ? (
                        <a
                          href={it.page}
                          target="_blank"
                          rel="noreferrer"
                          title={it.page}
                          className="hover:underline"
                        >
                          {it.page}
                        </a>
                      ) : (
                        <span title={it.page}>{it.page}</span>
                      )}
                    </LongTextCell>
                  </td>
                  <td className="px-2 py-2.5" data-testid={`feedback-message-${it.feedback_id}`}>
                    <LongTextCell id={`feedback-msg-${it.feedback_id}`} text={it.message} lines={3} maxWidth={384} minWidth="16rem" className="whitespace-pre-wrap" />
                  </td>
                  <td className="px-2 py-2.5">
                    {it.sentry_event_id ? (
                      sentryOrg ? (
                        <a
                          href={`https://sentry.io/organizations/${sentryOrg}/issues/?query=${it.sentry_event_id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-900"
                        >
                          <ExternalLink size={11} /> {it.sentry_event_id.slice(0, 8)}
                        </a>
                      ) : (
                        <span className="text-slate-400 font-mono text-[11px]">{it.sentry_event_id.slice(0, 8)}</span>
                      )
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-5 py-2.5">
                    <Select value={it.status} onValueChange={(v) => setStatus(it.feedback_id, v)}>
                      <SelectTrigger
                        aria-label="Status"
                        data-testid={`feedback-status-${it.feedback_id}`}
                        className="h-8 w-24 bg-white px-2 py-1 text-[12px]"
                      ><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table></TableScroller>
      )}
    </section>
  );
}
